import {
  type QueryClient,
  type UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

import { toastError } from "@alpacahq/ui/old";

import { queryKeys } from "../queryKeys";
import {
  type LiquidatePositionParams,
  liquidatePosition,
} from "../rest/account";
import { getErrorMessage } from "./errorParsing";
import { useMutationRequest } from "./useMutationRequest";

// The backend returns a 403 with this body when the requested quantity is held
// for open orders. It should probably be a 400, but the behaviour has existed
// for a while and we don't want to break existing integrations.
const liquidateInsufficientQuantityErrorDataSchema = z.object({
  available: z.coerce.number(),
  code: z.coerce.number().pipe(z.literal(40310000)),
  existing_qty: z.coerce.number(),
  held_for_orders: z.coerce.number(),
  message: z.string(),
  symbol: z.string(),
});

/**
 * Maps a liquidation error into a user-facing message. When the failure is the
 * "insufficient qty held for orders" case and the user did not opt to cancel
 * open orders, it appends a hint pointing them at the cancel-open-orders option.
 */
export const getLiquidationErrorMessage = (
  error: Error,
  variables?: Pick<LiquidatePositionParams, "cancelOrders">,
): string => {
  if (axios.isAxiosError(error) && error.response?.status === 403) {
    const { success, data } =
      liquidateInsufficientQuantityErrorDataSchema.safeParse(
        error.response?.data,
      );

    if (success) {
      const shouldSuggestCancelOrders =
        !variables?.cancelOrders && data.held_for_orders > 0;

      return shouldSuggestCancelOrders
        ? `${data.message}. Please try again and choose to cancel all open orders for this symbol.`
        : data.message;
    }
  }

  return getErrorMessage(error);
};

/**
 * Liquidating a position affects positions, open orders (when cancelled),
 * balances, and option positions, so invalidate all of them on success.
 */
export const invalidateLiquidationQueries = (client: QueryClient): void => {
  client.invalidateQueries({ queryKey: queryKeys.orders.all });
  client.invalidateQueries({ queryKey: queryKeys.positions.all });
  client.invalidateQueries({ queryKey: queryKeys.balances.all });
  client.invalidateQueries({ queryKey: queryKeys.options.all });
};

/**
 * Shared mutation for liquidating a position. Centralizes cache invalidation
 * and the user-facing error message mapping so every call site stays
 * consistent. Callers can still pass their own `onSuccess`/`onError`/`onSettled`
 * for UI concerns (toasts, modal close, loading state); the shared invalidation
 * and default error toast run alongside them.
 */
export const useLiquidatePosition = (
  options?: UseMutationOptions<unknown, Error, LiquidatePositionParams>,
) => {
  const client = useQueryClient();

  return useMutationRequest<unknown, LiquidatePositionParams>(
    (params) => liquidatePosition(params),
    {
      ...options,
      onSuccess: (...args) => {
        invalidateLiquidationQueries(client);
        options?.onSuccess?.(...args);
      },
      onError: (...args) => {
        const [error, variables] = args;
        if (options?.onError) {
          options.onError(...args);
          return;
        }

        toastError(getLiquidationErrorMessage(error, variables));
      },
    },
  );
};

export default useLiquidatePosition;
